import React, { useState, useEffect } from 'react';
import CollapsibleSection from './CollapsibleSection';
import { getAlertSettings, updateAlertSettings, resetAlertSettings } from '../services/api';

interface AlertSettingsProps {
  patientId: string;
}

interface AlertSettingsData {
  clustering_window_minutes: number;
  clustering_threshold: number;
  clustering_min_severity: string;
  grouping_logic: 'OR' | 'AND';
  grouped_vitals: string[];
  alert_cooldown_minutes: number;
  clustering_enabled: boolean;
}

const AlertSettings: React.FC<AlertSettingsProps> = ({ patientId }) => {
  const [settings, setSettings] = useState<AlertSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, [patientId]);

  const loadSettings = async () => {
    try {
      const data = await getAlertSettings(patientId);
      setSettings(data);
    } catch (error) {
      console.error('Error loading alert settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      await updateAlertSettings(patientId, settings);
      setEditing(false);
    } catch (error) {
      console.error('Error saving alert settings:', error);
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset alert settings to defaults?')) return;

    setSaving(true);
    try {
      const data = await resetAlertSettings(patientId);
      setSettings(data);
      setEditing(false);
    } catch (error) {
      console.error('Error resetting alert settings:', error);
      alert('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleVital = (vital: string) => {
    if (!settings) return;

    const grouped = settings.grouped_vitals || [];
    const newGrouped = grouped.includes(vital)
      ? grouped.filter(v => v !== vital)
      : [...grouped, vital];

    setSettings({ ...settings, grouped_vitals: newGrouped });
  };

  if (loading) {
    return <div className="text-gray-500">Loading alert settings...</div>;
  }

  if (!settings) {
    return <div className="text-red-500">Failed to load alert settings</div>;
  }

  const allVitals = [
    { key: 'heart_rate', label: 'Heart Rate' },
    { key: 'spo2', label: 'SpO2' },
    { key: 'blood_pressure', label: 'Blood Pressure' },
    { key: 'temperature', label: 'Temperature' },
    { key: 'respiratory_rate', label: 'Respiratory Rate' }
  ];

  return (
    <CollapsibleSection
      title="Alert Configuration"
      icon="🔔"
      actions={
        <>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Edit Settings
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setEditing(false);
                loadSettings();
              }}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
        </>
      }
    >
      <div className="space-y-4">
        {/* Clustering Enable/Disable */}
        <div className="flex items-center justify-between py-2 border-b">
          <div>
            <div className="font-medium text-gray-700">Alert Clustering</div>
            <div className="text-sm text-gray-500">Group similar alerts together</div>
          </div>
          {editing ? (
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.clustering_enabled}
                onChange={(e) => setSettings({ ...settings, clustering_enabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          ) : (
            <span className={`px-3 py-1 text-sm rounded-full ${settings.clustering_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {settings.clustering_enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>

        {/* Clustering Window */}
        <div className="flex items-center justify-between py-2">
          <div>
            <div className="font-medium text-gray-700">Clustering Window</div>
            <div className="text-sm text-gray-500">Time window for grouping alerts (minutes)</div>
          </div>
          {editing ? (
            <input
              type="number"
              min="1"
              max="120"
              value={settings.clustering_window_minutes}
              onChange={(e) => setSettings({ ...settings, clustering_window_minutes: parseInt(e.target.value) })}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-right"
            />
          ) : (
            <span className="text-gray-900 font-medium">{settings.clustering_window_minutes} min</span>
          )}
        </div>

        {/* Clustering Threshold */}
        <div className="flex items-center justify-between py-2">
          <div>
            <div className="font-medium text-gray-700">Clustering Threshold</div>
            <div className="text-sm text-gray-500">Number of alerts before clustering</div>
          </div>
          {editing ? (
            <input
              type="number"
              min="1"
              max="10"
              value={settings.clustering_threshold}
              onChange={(e) => setSettings({ ...settings, clustering_threshold: parseInt(e.target.value) })}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-right"
            />
          ) : (
            <span className="text-gray-900 font-medium">{settings.clustering_threshold} alerts</span>
          )}
        </div>

        {/* Alert Cooldown */}
        <div className="flex items-center justify-between py-2 border-b pb-4">
          <div>
            <div className="font-medium text-gray-700">Alert Cooldown</div>
            <div className="text-sm text-gray-500">Prevent repeat alerts (minutes)</div>
          </div>
          {editing ? (
            <input
              type="number"
              min="5"
              max="180"
              value={settings.alert_cooldown_minutes}
              onChange={(e) => setSettings({ ...settings, alert_cooldown_minutes: parseInt(e.target.value) })}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-right"
            />
          ) : (
            <span className="text-gray-900 font-medium">{settings.alert_cooldown_minutes} min</span>
          )}
        </div>

        {/* Grouping Logic */}
        <div className="pt-2">
          <div className="font-medium text-gray-700 mb-2">Alert Grouping Logic</div>
          <div className="flex items-center space-x-4 mb-3">
            {editing ? (
              <>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="grouping_logic"
                    value="OR"
                    checked={settings.grouping_logic === 'OR'}
                    onChange={(e) => setSettings({ ...settings, grouping_logic: 'OR' })}
                    className="mr-2"
                  />
                  <span className="text-sm">OR - Alert if ANY vital is abnormal</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="grouping_logic"
                    value="AND"
                    checked={settings.grouping_logic === 'AND'}
                    onChange={(e) => setSettings({ ...settings, grouping_logic: 'AND' })}
                    className="mr-2"
                  />
                  <span className="text-sm">AND - Alert only if ALL selected vitals are abnormal</span>
                </label>
              </>
            ) : (
              <div className="text-sm">
                <span className="font-semibold text-gray-900">{settings.grouping_logic}</span> logic:{' '}
                {settings.grouping_logic === 'OR'
                  ? 'Alert if any vital is abnormal'
                  : 'Alert only if all selected vitals are abnormal'}
              </div>
            )}
          </div>

          {/* Vital Selection */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-700 mb-2">Vitals to Monitor for Grouping:</div>
            <div className="grid grid-cols-2 gap-2">
              {allVitals.map(vital => (
                <label key={vital.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.grouped_vitals?.includes(vital.key) || false}
                    onChange={() => editing && toggleVital(vital.key)}
                    disabled={!editing}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{vital.label}</span>
                </label>
              ))}
            </div>
            {settings.grouped_vitals?.length === 0 && (
              <div className="text-xs text-amber-600 mt-2">
                ⚠️ No vitals selected - individual threshold alerts will apply
              </div>
            )}
          </div>
        </div>

        {/* Example Explanation */}
        {settings.grouped_vitals?.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <div className="text-sm font-medium text-blue-900 mb-1">Example:</div>
            <div className="text-sm text-blue-800">
              {settings.grouping_logic === 'OR' ? (
                <>
                  Alert will trigger if <strong>any</strong> of:{' '}
                  {settings.grouped_vitals.map((v, i) => (
                    <span key={v}>
                      {allVitals.find(vital => vital.key === v)?.label}
                      {i < settings.grouped_vitals.length - 1 ? ', ' : ''}
                    </span>
                  ))}{' '}
                  exceeds its threshold.
                </>
              ) : (
                <>
                  Alert will trigger <strong>only if all</strong> of:{' '}
                  {settings.grouped_vitals.map((v, i) => (
                    <span key={v}>
                      {allVitals.find(vital => vital.key === v)?.label}
                      {i < settings.grouped_vitals.length - 1 ? ', ' : ''}
                    </span>
                  ))}{' '}
                  exceed their thresholds simultaneously.
                </>
              )}
            </div>
          </div>
        )}

        {/* Reset Button */}
        {editing && (
          <div className="pt-4 border-t">
            <button
              onClick={handleReset}
              className="text-sm text-red-600 hover:text-red-800"
              disabled={saving}
            >
              Reset to Defaults
            </button>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default AlertSettings;
